/**
 * The signed-in creator's access token, as the editor last handed it over (setJwt); the renderer never refreshes it.
 * Also what the editor sent of the cache from page load, before the renderer's bridge listened (readAfter,
 * readHomeUntil), and whether the page has told the editor it loaded (rendererLoaded), which the editor answers with
 * readAfter.
 */
export class EditorSession {
  #jwt: string | null = null;
  #readAfter: number | null = null;
  #readHomeUntil: number | null = null;
  #rendererLoaded = false;

  /** window.lureoEditorJwt(): null until the first token. */
  getLatestJwt(): string | null {
    return this.#jwt;
  }

  /** EditorSession_GetJwt: the token, or "" before the first one. */
  getJwt(): string {
    return this.#jwt ?? "";
  }

  setLatestJwt(token: string): void {
    this.#jwt = token;
  }

  /** window.lureoEditorReadAfter: the highest readAfter the host has kept, null before one. */
  getLatestReadAfter(): number | null {
    return this.#readAfter;
  }

  setLatestReadAfter(cacheSeq: number): void {
    this.#readAfter = cacheSeq;
  }

  /** window.lureoEditorReadHomeUntil: the latest readHomeUntil the host has kept, null before one. */
  getLatestReadHomeUntil(): number | null {
    return this.#readHomeUntil;
  }

  setLatestReadHomeUntil(until: number): void {
    this.#readHomeUntil = until;
  }

  /** Whether the host has posted rendererLoaded. */
  hasSentRendererLoaded(): boolean {
    return this.#rendererLoaded;
  }

  sentRendererLoaded(): void {
    this.#rendererLoaded = true;
  }
}

/** The page's one session: the host writes it, the /api/v1 client reads it. */
export const editorSession = new EditorSession();

export function getLatestJwt(): string | null {
  return editorSession.getLatestJwt();
}

export function getLatestReadAfter(): number | null {
  return editorSession.getLatestReadAfter();
}

export function getLatestReadHomeUntil(): number | null {
  return editorSession.getLatestReadHomeUntil();
}

export function hasSentRendererLoaded(): boolean {
  return editorSession.hasSentRendererLoaded();
}
